import React from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  HeartIcon,
  UsersIcon,
  AwardIcon,
  TrendingUpIcon,
  CheckCircleIcon,
  ShieldIcon
} from "lucide-react";
import Breadcrumb from "../Breadcrumb";

const AboutUs = () => {
  // Timeline disabled; keep only Founded 2025
  const milestones = [
    { year: "2025", event: "Founded", description: "TheLivCure established with a patient-first vision" }
  ];

  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "description": "India's leading surgical care platform providing advanced, minimally invasive treatments with personalized patient care and world-class medical expertise.",
      "foundingDate": "2025",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "G-627, Sangam Vihar",
        "addressLocality": "New Delhi",
        "postalCode": "110062",
        "addressCountry": "IN"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+919311720045",
        "contactType": "customer service"
      }
    }
  };

  const values = [
    {
      icon: HeartIcon,
      title: "Patient-Centric Care",
      description: "Every decision we make is centered around improving patient outcomes and experience"
    },
    {
      icon: ShieldIcon,
      title: "Safety & Quality",
      description: "Maintaining the highest standards of safety and quality in all our medical procedures"
    },
    {
      icon: UsersIcon,
      title: "Expert Team",
      description: "Working with the best medical professionals and healthcare experts in India"
    },
    {
      icon: TrendingUpIcon,
      title: "Innovation",
      description: "Continuously adopting latest medical technology and innovative treatment methods"
    }
  ];

  const leadership = [
    {
      name: "Rajkumar pandey",
      position: "CEO",
      description: "Leads operations with a patient-first approach",
      image: "leadership1"
    },
    {
      name: "Mohit pandey",
      position: "Co-founder",
      description: "Drives service quality and seamless care delivery",
      image: "leadership2"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Us"
        description="Learn about TheLivCure - India's leading surgical care platform. Discover our mission to provide advanced, minimally invasive treatments with personalized patient care and world-class medical expertise."
        keywords="about thelivcure, medical organization, surgical care platform, healthcare mission, patient care, medical expertise, Delhi medical center"
        url="/about"
        structuredData={aboutStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: "About Us" }
            ]} 
          />
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              About <span className="text-blue-600">TheLivCure</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
              Where Care Comes First — a progressive healthcare organization based in Delhi, delivering
              accessible, high-quality services with a patient-first approach.
            </p>
            {/* Stats grid temporarily disabled */}
            {false && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">3M+</div>
                <div className="text-xs sm:text-sm text-gray-600">Happy Patients</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">400+</div>
                <div className="text-xs sm:text-sm text-gray-600">Expert Doctors</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600">150+</div>
                <div className="text-xs sm:text-sm text-gray-600">Clinics</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600">30+</div>
                <div className="text-xs sm:text-sm text-gray-600">Cities</div>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            <Card className="border-2 border-blue-100">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <HeartIcon className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-600 leading-relaxed">
                  To make advanced surgical care accessible, affordable, and stress-free for every patient. 
                  We are committed to providing world-class medical treatment with personalized care, 
                  ensuring faster recovery and better outcomes through innovative technology and experienced healthcare professionals.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <AwardIcon className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-600 leading-relaxed">
                  To become India's most trusted healthcare platform, revolutionizing the surgical experience 
                  through technology-enabled care. We envision a future where quality healthcare is accessible 
                  to all, supported by compassionate care and cutting-edge medical expertise.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-lg text-gray-600">Key milestones in our growth story</p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{milestone.event}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="relative z-10">
                    <div className="w-6 h-6 bg-blue-600 rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Leadership</h2>
            <p className="text-lg text-gray-600">Meet the visionaries driving healthcare innovation</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {leadership.map((leader, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <UsersIcon className="w-16 h-16 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{leader.name}</h3>
                  <div className="text-blue-600 font-medium mb-3">{leader.position}</div>
                  <p className="text-gray-600 text-sm">{leader.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience World-Class Care?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join millions of satisfied patients who chose TheLivCure for their surgical needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Book Free Consultation
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              Find Doctors Near You
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;